import React from 'react';
import RaisedButton from 'material-ui/RaisedButton';
import PropTypes from 'prop-types';
import Formsy from 'formsy-react';
import { Typography, Box } from '@material-ui/core';
import FlatButton from 'material-ui/FlatButton';
import { FormsyText } from 'formsy-material-ui/lib';
import ApiSelector from '../../../containers/common/selectors/selectorContainer';
import { refSetter, doubleActionPrevent } from '../../../utils/helpers';
import Table from '../../../containers/common/tables/tableContainer';
import { teamsEndpoint, getExcludedCliniciansForPatient, cliniciansEndpoint } from '../../../settings/constants';
import { textFieldStyle } from '../../../theme/AllocateBaseTheme';
import globals from '../../../globals/communityGlobals';

const locale = globals.getArea('patients');
const errorsLocale = globals.getArea('error');

const maxReasonLength = 100;
const errorMessages = {
  maxLength: errorsLocale.getMsg('validationMaxLength', [`${maxReasonLength}`]),
};

class ExcludedClinicians extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      canSubmit: false,
      isCliniciansFetchRequired: true,
      reloadData: false,
    };

    this.setRef = refSetter.bind(this);
  }

  enableButton = () => {
    this.setState({
      canSubmit: true,
    });
  };

  disableButton = () => {
    this.setState({
      canSubmit: false,
    });
  };

  handleAdd = doubleActionPrevent.bind(this)(
    'Add',
    async model => {
      const { patient } = this.props;

      await this.props.excludeClinician(patient.id, model.clinicianId, model.reason);
    },
    () => {
      this.setState({ reloadData: !this.state.reloadData });
      this.form.reset();
    },
  );

  handleRemove = (patientId, clinicianId) =>
    doubleActionPrevent.bind(this)(
      'Remove',
      async event => {
        const { removeExcludedClinician } = this.props;
        event.stopPropagation();

        await removeExcludedClinician(patientId, clinicianId);
      },
      () => {
        this.setState({ reloadData: !this.state.reloadData });
      },
    );

  handleTeamsChange = (event, value, payload) => {
    this.setState({ teamIds: payload, isCliniciansFetchRequired: true });
  };

  render() {
    const { patient, canEditPatients } = this.props;
    const { teamIds } = this.state;
    const columns = [
      {
        Header: locale.getMsg('clinician'),
        accessor: 'clinicianName',
        width: 300,
      },
      {
        Header: locale.getMsg('reason'),
        accessor: 'reason',
        width: 500,
      },
      {
        Header: locale.getMsg('action'),
        id: 'action',
        width: 100,
        accessor: row => (
          <FlatButton
            style={{ fontWeight: 'bold', color: 'red' }}
            role="button"
            primary
            onClick={this.handleRemove(patient.id, row.clinicianId)}
            disabled={!canEditPatients}
          >
            {locale.getMsg('remove')}
          </FlatButton>
        ),
      },
    ];
    return (
      <div className="tab-box">
        <Box p={1} style={{ marginTop: '16px' }}>
          <Typography variant="h6">{locale.getMsg('excludedCliniciansHeader')}</Typography>
          <Typography variant="subtitle1">
            {locale.getMsg('excludedCliniciansDescription', [patient.patientFullName])}
          </Typography>
        </Box>
        <Formsy.Form
          onValidSubmit={model => this.handleAdd(model)}
          onValid={this.enableButton}
          onInvalid={this.disableButton}
          ref={this.setRef('form')}
        >
          <Box display="flex">
            <Box p={1} flexGrow={1}>
              <ApiSelector
                reduxIdentifier="teams"
                endpoint={teamsEndpoint}
                multiple
                floatingLabelText={`${locale.getMsg('teams')}`}
                name="orgUnitIds"
                isFetchRequired
                onChange={this.handleTeamsChange}
                value={(teamIds || []).map(id => ({ id }))}
              />
            </Box>
            <Box p={1} flexGrow={1}>
              <ApiSelector
                name="clinicianId"
                endpoint={cliniciansEndpoint}
                reduxIdentifier="excludedClinicianId"
                required
                floatingLabelText={locale.getMsg('clinician')}
                isFetchRequired={this.state.isCliniciansFetchRequired}
                callArgs={{ teamIds: this.state.teamIds, patientId: patient.id }}
                disabled={!canEditPatients}
                multiple
              />
            </Box>
            <Box p={1} flexGrow={1}>
              <FormsyText
                name="reason"
                className="formsytext-css"
                validations={{ maxLength: maxReasonLength }}
                validationErrors={errorMessages}
                floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                style={{
                  ...textFieldStyle.style,
                  width: '100%',
                }}
                inputStyle={textFieldStyle.inputStyle}
                underlineStyle={textFieldStyle.underlineStyle}
                underlineFocusStyle={textFieldStyle.underlineFocusStyle}
                validationColor={textFieldStyle.validationColor}
                floatingLabelText={locale.getMsg('reason')}
                disabled={!canEditPatients}
              />
            </Box>
            <Box p={1}>
              <RaisedButton
                primary
                label={locale.getMsg('add')}
                type="submit"
                disabled={!canEditPatients || !this.state.canSubmit}
              />
            </Box>
          </Box>
        </Formsy.Form>

        <Table
          triggerReload={this.state.reloadData}
          columns={columns}
          endpoint={getExcludedCliniciansForPatient}
          reduxIdentifier="excludedClinicians"
          initialSearchParams={{
            pageNumber: 1,
            pageSize: 10,
            patientId: patient.id,
          }}
        />
      </div>
    );
  }
}

ExcludedClinicians.propTypes = {
  patient: PropTypes.shape({
    patientFullName: PropTypes.string,
  }).isRequired,
  removeExcludedClinician: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  excludeClinician: PropTypes.func.isRequired,
  canEditPatients: PropTypes.bool.isRequired,
};

export default ExcludedClinicians;
